import { Menu, Transition } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Fragment, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { NavItem } from './types';

interface Props {
  items: NavItem[];
  onClick?: () => void;
  short?: boolean;
}

function OverflowNavMenu(props: Props) {
  const { items, short, onClick = () => null } = props;
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();

  if (items.length === 0) {
    return null;
  }

  const isActiveGroup = items.some((item) =>
    location.pathname.startsWith(item.href)
  );

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button
            className={clsx(
              open || isActiveGroup
                ? 'dark:text-dark-text bg-gray-200 text-gray-700 dark:bg-gray-700'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
              'focus-visible:ring-primary-500 dark:focus:ring-offset-dark-surface group flex items-center rounded-md px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              !short && 'w-full'
            )}
            title="More options"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <EllipsisHorizontalIcon
              className={clsx(
                open || isActiveGroup
                  ? 'dark:text-dark-text text-gray-700'
                  : 'text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300',
                !short && 'mr-3',
                'h-6 w-6 flex-shrink-0'
              )}
              aria-hidden="true"
            />
            {short ? '' : 'More'}
          </Menu.Button>

          <Transition
            show={open || isHovered}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 -translate-x-2"
            enterTo="transform opacity-100 translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="transform opacity-100 translate-x-0"
            leaveTo="transform opacity-0 -translate-x-2"
          >
            <Menu.Items
              className={clsx(
                'border-onlight-line-2 dark:border-ondark-line-2 absolute z-20 w-56 origin-left rounded-xl border py-2 ring-1 ring-opacity-10 drop-shadow-lg',
                short
                  ? 'left-16 top-1/2 -translate-y-1/2'
                  : 'left-full top-1/2 ml-4 -translate-y-1/2',
                'dark:bg-ondark-bg-1 bg-onlight-bg-1 dark:ring-ondark-line-1 ring-onlight-line-2'
              )}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {items.map((item) => (
                <Menu.Item key={item.name} as="div">
                  {({ close }) => (
                    <NavLink
                      to={item.href}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                        setIsHovered(false);
                        close();
                      }}
                      className={({ isActive }) =>
                        clsx(
                          'group flex items-center py-3 text-sm font-medium transition-colors duration-150',
                          isActive
                            ? 'dark:text-dark-text mx-2 rounded-md bg-gray-200 px-2 text-gray-700 dark:bg-gray-700'
                            : 'px-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        )
                      }
                    >
                      <item.icon
                        className="mr-3 h-5 w-5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      {item.name}
                    </NavLink>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}

export default OverflowNavMenu;
